import { useState, useEffect } from "react";
import { BiInfoCircle } from "react-icons/bi";
import { BsUpload } from "react-icons/bs";
import { X } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createStoreUrl, uploadStoreImageUrl, createWarehouseUrl, generateLogoUrl } from "../../api/endpoints";
import DeliverToggle from "../../components/toggleButton/DeliverToggle";
import { refreshUserToken } from "../../tools/refreshToken";
import { setUser } from "../../slice/webStoreUserSlice";
import Spinner, { ButtonLoader, ButtonLoaderII } from "../../components/uxCollections/Loaders";
import ImageCropper from "../../components/imageCropper/ImageCropper";
import { listOfProductCategory } from "./stockManagement/AddStockPage";
import { uiAlert } from "../../components/alerts/customAlert/uiAlert";
import "./CreateStore.css";

/* ─── Constants ──────────────────────────────────────────────── */

const warehouseIconUrl = [
  "https://cdn.dribbble.com/users/101153/screenshots/11104620/warehouse.png",
  "https://res.cloudinary.com/ebuka1122/image/upload/v1748125616/allAssets/003-warehouse-2_z59jsh.png",
  "https://res.cloudinary.com/ebuka1122/image/upload/v1748125615/allAssets/001-warehouse_ztuuzw.png",
  "https://res.cloudinary.com/ebuka1122/image/upload/v1748125612/allAssets/002-warehouse-1_hifv9n.png",
  "https://res.cloudinary.com/ebuka1122/image/upload/v1748125610/allAssets/015-warehouse-storage_vtkncw.png",
  "https://res.cloudinary.com/ebuka1122/image/upload/v1748125608/allAssets/013-warehouse-9_ug2whv.png",
  "https://res.cloudinary.com/ebuka1122/image/upload/v1748125607/allAssets/014-data-warehouse_b7eflz.png",
  "https://res.cloudinary.com/ebuka1122/image/upload/v1748125605/allAssets/012-warehouse-8_zad1xl.png",
  "https://res.cloudinary.com/ebuka1122/image/upload/v1748125603/allAssets/010-storage_p3ddh9.png",
  "https://res.cloudinary.com/ebuka1122/image/upload/v1748125601/allAssets/009-warehouse-7_wfv7c0.png",
  "https://res.cloudinary.com/ebuka1122/image/upload/v1748125599/allAssets/006-warehouse-4_a72esx.png",
  "https://res.cloudinary.com/ebuka1122/image/upload/v1748125598/allAssets/008-warehouse-6_yw8z0k.png",
  "https://res.cloudinary.com/ebuka1122/image/upload/v1748125596/allAssets/011-distribution_mpx4tt.png",
  "https://res.cloudinary.com/ebuka1122/image/upload/v1748125594/allAssets/005-inventory_gb51wb.png",
  "https://res.cloudinary.com/ebuka1122/image/upload/v1748125592/allAssets/007-warehouse-5_sdmdhk.png",
  "https://res.cloudinary.com/ebuka1122/image/upload/v1748125591/allAssets/004-warehouse-3_varos4.png",
];
let whIconCursor = 0;

const logoColors = [
  { name: "Navy Blue", hex: "#1e3a5f" },
  { name: "Sky Blue", hex: "#4A90E2" },
  { name: "Royal Purple", hex: "#6d28d9" },
  { name: "Forest Green", hex: "#15803d" },
  { name: "Crimson Red", hex: "#b91c1c" },
  { name: "Burnt Orange", hex: "#c2410c" },
  { name: "Teal", hex: "#0f766e" },
  { name: "Hot Pink", hex: "#be185d" },
  { name: "Slate Gray", hex: "#475569" },
  { name: "Gold", hex: "#b45309" },
  { name: "Indigo", hex: "#3730a3" },
  { name: "Charcoal", hex: "#1f2937" },
];

/* ─── Shared UI primitives ───────────────────────────────────── */

function Field({ label, value, onChange, placeholder, maxLength }) {
  const [focused, setFocused] = useState(false);
  return (
    <div className='cswh__field-wrap'>
      <label className={`cswh__field-label${focused ? " cswh__field-label--focused" : ""}`}>{label}</label>
      <input
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={`cswh__field-input${focused ? " cswh__field-input--focused" : ""}`}
      />
    </div>
  );
}

function InfoBanner({ type, children }) {
  return (
    <div className={`cswh__banner cswh__banner--${type === "blue" ? "blue" : "orange"}`}>
      <BiInfoCircle size={16} className='cswh__banner-icon' />
      <span>{children}</span>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div className={`cswh__toggle-track${value ? " cswh__toggle-track--on" : ""}`} onClick={() => onChange(!value)}>
      <div className={`cswh__toggle-thumb${value ? " cswh__toggle-thumb--on" : ""}`} />
    </div>
  );
}

function PrimaryBtn({ children, onClick, disabled }) {
  return (
    <button className='cswh__btn-primary' onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

function StoreSVG() {
  return (
    <svg viewBox='0 0 64 64' fill='none' width='52' height='52'>
      <rect x='8' y='28' width='48' height='28' rx='3' fill='#ffe4c8' stroke='#f4a261' strokeWidth='1.5' />
      <path d='M4 28L12 16h40l8 12H4Z' fill='#ffd8b0' stroke='#f4a261' strokeWidth='1.5' />
      <rect x='22' y='38' width='20' height='18' rx='2' fill='#fff' stroke='#f4a261' strokeWidth='1.2' />
      <circle cx='44' cy='18' r='4' fill='#f4a261' />
      <line x1='44' y1='10' x2='44' y2='22' stroke='#f4a261' strokeWidth='1.5' />
    </svg>
  );
}

/* ─── Color dropdown ────────────────────────────────────────── */

function ColorDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = logoColors.find((c) => c.hex === value) || logoColors[0];

  return (
    <div className='cswh__color-wrap'>
      <label className='cswh__color-label'>Logo color</label>
      <div className='cswh__color-trigger' onClick={() => setOpen((o) => !o)}>
        <div className='cswh__color-swatch' style={{ background: selected.hex }} />
        <span className='cswh__color-name'>{selected.name}</span>
        <span className='cswh__color-caret'>▾</span>
      </div>

      {open && (
        <div className='cswh__color-dropdown'>
          {logoColors.map((c) => (
            <div
              key={c.hex}
              className={`cswh__color-option${c.hex === value ? " cswh__color-option--selected" : ""}`}
              onClick={() => {
                onChange(c.hex);
                setOpen(false);
              }}
            >
              <div className='cswh__color-swatch' style={{ background: c.hex }} />
              <span className='cswh__color-option-name'>{c.name}</span>
              <span className='cswh__color-option-hex'>{c.hex}</span>
              {c.hex === value && <span className='cswh__color-option-check'>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Generate Store Logo AI modal ──────────────────────────── */

const GenerateStoreLogoAI = ({ closeModal, params, updateStoreLogo, updateIsLogoAiGenerated }) => {
  const [logoParams, setLogoParams] = useState({ slogan: "", description: "", LogoColor: "#1e3a5f" });
  const [generatedLogo, setGeneratedLogo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    setIsFormValid(!!(logoParams.slogan && logoParams.description));
  }, [logoParams]);

  const generateLogoFun = async () => {
    setIsLoading(true);
    const payload = {
      imageText: logoParams.slogan,
      LogoColor: logoParams.LogoColor,
      storeName: params.storeName,
      category: params.storeCategory,
    };
    try {
      const response = await fetch(generateLogoUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to generate logo");
      const { logoUrl } = await response.json();
      setGeneratedLogo(logoUrl);
    } catch (error) {
      console.log(error);
      uiAlert("Failed to generate logo, try again later", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const useGeneratedLogo = () => {
    updateStoreLogo(generatedLogo);
    updateIsLogoAiGenerated(true);
    closeModal(false);
  };

  return (
    <div className='cswh__ai-backdrop'>
      <div className='cswh__ai-card'>
        {/* Header */}
        <div className='cswh__ai-header'>
          <span className='cswh__ai-title'>Generate Store Logo</span>
          <button className='cswh__close-btn' onClick={() => closeModal(false)}>
            ✕
          </button>
        </div>

        {/* Logo preview */}
        <div className='cswh__ai-preview-wrap'>
          <div className='cswh__ai-preview-box'>{generatedLogo ? <img src={generatedLogo} alt='generated logo' /> : <StoreSVG />}</div>
          <p className='cswh__ai-preview-caption'>Complete form to generate logo</p>
        </div>

        {/* Slogan */}
        <Field
          label='Store Slogan (10 characters max) *'
          value={logoParams.slogan}
          onChange={(v) => setLogoParams((p) => ({ ...p, slogan: v }))}
          placeholder='e.g. Shop Smart'
          maxLength={10}
        />

        {/* Description */}
        <div className='cswh__textarea-wrap'>
          <label className='cswh__textarea-label'>Description of store activity (80 char max) *</label>
          <textarea
            className='cswh__textarea'
            value={logoParams.description}
            maxLength={80}
            onChange={(e) => setLogoParams((p) => ({ ...p, description: e.target.value }))}
            placeholder='e.g. We sell quality automotive parts and accessories'
          />
        </div>

        {/* Color picker */}
        <ColorDropdown value={logoParams.LogoColor} onChange={(v) => setLogoParams((p) => ({ ...p, LogoColor: v }))} />

        {/* Action buttons */}
        {generatedLogo ? (
          <PrimaryBtn onClick={useGeneratedLogo}>Use logo as store logo</PrimaryBtn>
        ) : (
          <PrimaryBtn onClick={generateLogoFun} disabled={!isFormValid || isLoading}>
            {isLoading ? <ButtonLoader /> : "Generate logo with AI"}
          </PrimaryBtn>
        )}

        <button className='cswh__btn-secondary' disabled={isLoading} onClick={() => !isLoading && closeModal(false)}>
          Upload my logo instead
        </button>
      </div>
    </div>
  );
};

/* ─── Create Store card ──────────────────────────────────────── */

const CreateStoreCard = ({ closeModal }) => {
  const [storeName, setStoreName] = useState("");
  const [storeAddress, setStoreAddress] = useState("");
  const [storeCategory, setStoreCategory] = useState(null);
  const [storeImage, setStoreImage] = useState(null);
  const [storeImageBuffer, setStoreImageBuffer] = useState(null);
  const [isOnlineStoreOnly, setIsOnlineStoreOnly] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isImageCropperOpen, setIsImageCropperOpen] = useState(false);
  const [openLogoGenerator, setOpenLogoGenerator] = useState(false);
  const [isLogoAiGenerated, setIsLogoAiGenerated] = useState(false);

  const { user } = useSelector((state) => state.webStoreUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    setIsFormValid(!!(storeName && storeAddress && storeCategory && storeImage));
  }, [storeName, storeAddress, storeCategory, storeImage]);

  const handleImageUpload = (e) => {
    const image = e.target.files[0];
    setStoreImageBuffer(image);
    if (image) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        setStoreImage(evt.target.result);
        setIsImageCropperOpen(true);
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const createStore = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!isFormValid) {
      uiAlert("form is not valid", "warning");
      return;
    }
    setIsLoading(true);
    try {
      const uploadedImage = new FormData();
      uploadedImage.append("storeImage", storeImageBuffer);
      const response = await fetch(uploadStoreImageUrl, {
        method: "POST",
        headers: { authorization: `Bearer ${accessToken}` },
        body: uploadedImage,
      });
      if (response.ok) {
        const { imageUrl } = await response.json();
        const newStore = {
          storeName,
          storeAddress,
          storeCategory,
          storeImage: imageUrl,
          isOnlineStoreOnly,
          rating: 0,
          followers: 0,
          storeOwner: user._id,
        };
        const res2 = await fetch(createStoreUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", authorization: `Bearer ${accessToken}` },
          body: JSON.stringify(newStore),
        });
        if (res2.ok) {
          dispatch(setUser(await res2.json()));
          closeModal(false);
        }
      } else if (response.status === 400) {
        refreshUserToken(uploadStoreImageUrl, createStore, navigate);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const createStoreWithAiGeneratedLogo = async () => {
    if (!isFormValid) {
      uiAlert("form is not valid", "warning");
      return;
    }
    setIsLoading(true);
    const newStore = {
      storeName,
      storeAddress,
      storeCategory,
      storeImage,
      isOnlineStoreOnly,
      rating: 0,
      followers: 0,
      storeOwner: user._id,
    };
    try {
      const res = await fetch(createStoreUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", authorization: `Bearer ${localStorage.getItem("accessToken")}` },
        body: JSON.stringify(newStore),
      });
      if (res.ok) {
        dispatch(setUser(await res.json()));
        closeModal(false);
      } else if (res.status === 400) {
        refreshUserToken(createStoreUrl, createStoreWithAiGeneratedLogo, navigate);
      } else {
        uiAlert("something went wrong, try again later", "error");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const aiEnabled = !!(storeName && storeCategory);
  const canCreate = user.store.length < user.maxStore && isFormValid;

  return (
    <div className='cswh__body'>
      <Field label='Shop name (24 character max) *' value={storeName} onChange={setStoreName} placeholder='e.g. Ikem Electronics' maxLength={24} />
      <Field label='Shop address *' value={storeAddress} onChange={setStoreAddress} placeholder='e.g. No 19 Chukwu street Abakaliki' />

      {/* Category */}
      <div className='cswh__select-wrap'>
        <select
          className={`cswh__select${!storeCategory ? " cswh__select--placeholder" : ""}`}
          value={storeCategory || ""}
          onChange={(e) => setStoreCategory(e.target.value)}
        >
          <option value='' disabled hidden>
            Choose category
          </option>
          {listOfProductCategory.map((cat, i) => (
            <option key={i} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <span className='cswh__select-arrow'>▾</span>
      </div>

      {/* Logo row */}
      <div className='cswh__logo-row'>
        <div className='cswh__logo-preview'>{storeImage ? <img src={storeImage} alt='store logo' /> : <StoreSVG />}</div>
        <div className='cswh__logo-actions'>
          <button className={`cswh__btn-ai${aiEnabled ? "" : " cswh__btn-ai--disabled"}`} onClick={() => aiEnabled && setOpenLogoGenerator(true)}>
            ⚡ Generate logo with AI
          </button>
          <label className='cswh__btn-upload'>
            <BsUpload size={14} /> Upload logo
            <input onChange={handleImageUpload} name='storeImage' type='file' accept='image/*' />
          </label>
        </div>
      </div>

      {storeImage && (
        <span className='cswh__remove-link' onClick={() => setStoreImage(null)}>
          Remove
        </span>
      )}

      {/* Online store toggle */}
      <div className='cswh__toggle-row'>
        <span className='cswh__toggle-label'>Online store only</span>
        <DeliverToggle toggleValue={isOnlineStoreOnly} updatetoggle={setIsOnlineStoreOnly} />
      </div>

      {/* Info banner */}
      <InfoBanner type={user.store.length < user.maxStore ? "blue" : "orange"}>
        {user.store.length < user.maxStore ? "With a premium package you can create up to 10 stores" : "Maximum allowed store has been created"}
      </InfoBanner>

      {/* Create button */}
      <div className='cswh__spinner-wrap'>
        <PrimaryBtn onClick={isLogoAiGenerated ? createStoreWithAiGeneratedLogo : createStore} disabled={!canCreate}>
          {isLoading ? "Creating…" : "Create store"}
        </PrimaryBtn>
        {isLoading && <Spinner position='absolute' />}
      </div>

      {/* Image cropper */}
      {isImageCropperOpen && (
        <ImageCropper
          readImageUrl={storeImage}
          setCroppedImageUrl={setStoreImage}
          setCroppedImageBolb={setStoreImageBuffer}
          closeModal={setIsImageCropperOpen}
        />
      )}

      {/* AI logo generator */}
      {openLogoGenerator && (
        <GenerateStoreLogoAI
          closeModal={setOpenLogoGenerator}
          params={{ storeName, storeCategory }}
          updateStoreLogo={setStoreImage}
          updateIsLogoAiGenerated={setIsLogoAiGenerated}
        />
      )}
    </div>
  );
};

/* ─── Create Warehouse card ──────────────────────────────────── */

const CreateWarehouse = ({ closeModal }) => {
  const { user } = useSelector((state) => state.webStoreUser);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [warehouseName, setWarehouseName] = useState("");
  const [warehouseAddress, setWarehouseAddress] = useState("");
  const [warehouseIcon, setWarehouseIcon] = useState(warehouseIconUrl[Math.floor(Math.random() * warehouseIconUrl.length)]);
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    setIsFormValid(!!(warehouseName && warehouseAddress && warehouseIcon));
  }, [warehouseName, warehouseAddress, warehouseIcon]);

  const createWarehouse = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!isFormValid) {
      uiAlert("form is not valid", "warning");
      return;
    }
    try {
      setIsLoading(true);
      const res = await fetch(createWarehouseUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ warehouseName, warehouseAddress, warehouseIcon }),
      });
      if (res.ok) {
        dispatch(setUser(await res.json()));
        closeModal(false);
        uiAlert("Warehouse created successfully", "success");
      } else if (res.status === 400) {
        refreshUserToken(createWarehouseUrl, createWarehouse, navigate);
      }
    } catch (error) {
      console.log(error);
      return uiAlert("Failed to create warehouse, try again later", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const changeIcon = () => {
    if (whIconCursor >= warehouseIconUrl.length) whIconCursor = 0;
    setWarehouseIcon(warehouseIconUrl[whIconCursor]);
    whIconCursor++;
  };

  const canCreate = user.maxWarehouse !== 0 && user.warehouse.length < user.maxWarehouse && isFormValid;

  return (
    <div className='cswh__body'>
      {/* Icon row */}
      <div className='cswh__wh-icon-row'>
        <div className='cswh__wh-icon-preview'>
          <img src={warehouseIcon} alt='warehouse icon' />
        </div>
        <button className='cswh__btn-change-icon' onClick={changeIcon}>
          🔄 Change icon
        </button>
      </div>

      <Field label='Warehouse name *' value={warehouseName} onChange={setWarehouseName} placeholder='e.g. Main Storage Hub' />
      <Field label='Warehouse address *' value={warehouseAddress} onChange={setWarehouseAddress} placeholder='e.g. Plot 5, Industrial Layout' />

      <InfoBanner type={user.warehouse.length < user.maxWarehouse ? "blue" : "orange"}>
        {user.warehouse.length < user.maxWarehouse ? "With a premium package you can create up to 5 warehouses" : "Maximum allowed warehouse has been created"}
      </InfoBanner>

      <PrimaryBtn onClick={createWarehouse} disabled={!canCreate || isLoading}>
        {isLoading ? <ButtonLoaderII /> : "Create warehouse"}
      </PrimaryBtn>
    </div>
  );
};

/* ─── Root: tabs + modal shell ───────────────────────────────── */

const CreateStore = ({ closeModal }) => {
  const [isStore, setIsStore] = useState(true);

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) closeModal(false);
  };

  return (
    <div className='cswh__backdrop' onClick={handleBackdrop}>
      <div className='cswh__card' onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className='cswh__header'>
          <span className='cswh__header-title'>Create Store or Warehouse</span>
          <button className='cswh__close-btn' onClick={() => closeModal(false)}>
            <X size={18} />
          </button>
        </div>

        {/* Tab switcher */}
        <div className='cswh__tabs'>
          {[
            { label: "Store", val: true },
            { label: "Warehouse", val: false },
          ].map(({ label, val }) => (
            <button key={label} className={`cswh__tab-btn${isStore === val ? " cswh__tab-btn--active" : ""}`} onClick={() => setIsStore(val)}>
              {label}
            </button>
          ))}
        </div>

        {/* Panel */}
        {isStore ? <CreateStoreCard closeModal={closeModal} /> : <CreateWarehouse closeModal={closeModal} />}
      </div>
    </div>
  );
};

export default CreateStore;
